<script setup>
import { computed, ref } from 'vue';
import formatDate from '@/utils/format-date.ts';
import { isValid, PLACEMENTS } from '@/components/base-components/utils/placement.ts';
import { HOURS_FORMAT, SIZE, TYPE } from '@/components/base-components/components/time-picker/index.js';
import BaseTimePickerHours from '@/components/base-components/components/time-picker/components/hours.vue';
import BaseTimePickerMinutes from '@/components/base-components/components/time-picker/components/minutes.vue';
import BaseTimePickerSeconds from '@/components/base-components/components/time-picker/components/seconds.vue';
import BaseTimePickerAmPm from '@/components/base-components/components/time-picker/components/am-pm.vue';

const props = defineProps({
    value:          {
        type: Date,
    },
    type:           {
        type:      String,
        default:   TYPE.DEFAULT,
        validator: value => Object.values(TYPE).includes(value),
    },
    size:           {
        type:      String,
        default:   SIZE.SMALL,
        validator: value => Object.values(SIZE).includes(value),
    },
    hoursFormat:    {
        type:      String,
        default:   HOURS_FORMAT.HOURS_24,
        validator: value => Object.values(HOURS_FORMAT).includes(value),
    },
    minutesStep:    {
        type:      Number,
        default:   1,
        validator: value => value > 0 && value <= 60,
    },
    withSeconds:    {
        type: Boolean,
    },
    placeholder:    {
        type:    String,
        default: 'Pick a Time...',
    },
    disabled:       {
        type: Boolean,
    },
    clearable:      {
        type: Boolean,
    },
    noSubmit:       {
        type: Boolean,
    },
    placement:      {
        type:      String,
        default:   PLACEMENTS.BOTTOM_START,
        validator: isValid,
    },
    noClickOutside: {
        type: Boolean,
    },
    noHideOnClick:  {
        type: Boolean,
    },
    render:         {
        type:      String,
        default:   'default',
        validator: value => ['default', 'lazy', 'always'].includes(value),
    },
    openDelay:      {
        type:    Number,
        default: 0,
    },
    hideDelay:      {
        type:    Number,
        default: 50,
    },
    transition:     {
        type: Object,
        default() {
            return {
                'enter-active-class': 'transition duration-150 ease-in-out origin-top scale-y-100',
                'leave-active-class': 'transition duration-150 ease-in-out origin-top scale-y-100',
                'leave-to-class':     'opacity-0 origin-top scale-y-50',
                'enter-class':        'opacity-0 origin-top scale-y-50',
            };
        },
    },
});

const emit = defineEmits({
    input: null,
});

const opened = ref(false);

function changeOpened() {
    if (props.disabled) return;

    opened.value = !opened.value;
}

const referenceHover = ref(false);

function onReferenceHover(value) {
    if (props.disabled) return;

    referenceHover.value = value;
}

const showClear = computed(() => props.clearable && referenceHover.value && model.value);

const model = computed({
    get: () => props.value,
    set: value => emit('input', value),
});

function clear() {
    model.value = null;
}

function onNow() {
    const now = new Date();

    now.setFullYear(model.value.getFullYear());
    now.setMonth(model.value.getMonth());
    now.setDate(model.value.getDate());
    now.setSeconds(props.withSeconds ? now.getSeconds() : 0);

    model.value = now;
}

const formattedValue = computed(() => {
    if (!model.value) return;

    return formatDate(model.value.toISOString(), {
        hourCycle: props.hoursFormat === HOURS_FORMAT.HOURS_24 ? 'h23' : 'h12',
        hour:      'numeric',
        minute:    'numeric',
        ...(props.withSeconds && {
            second: 'numeric',
        }),
    });
});

function onHidden() {
}

const typeClass = computed(() => {
    if (!opened.value) return 'border-gray-400/40';

    return {
        [TYPE.DEFAULT]:       'border-science-blue-800',
        [TYPE.PRIMARY]:       'border-primary-600',
        [TYPE.SECONDARY]:     'border-secondary-950',
        [TYPE.PRIMARY_OLD]:   'border-science-blue-500',
        [TYPE.SECONDARY_OLD]: 'border-science-blue-700',
        [TYPE.SUCCESS]:       'border-emerald-600',
        [TYPE.WARNING]:       'border-orange-500',
        [TYPE.DANGER]:        'border-red-600',
        [TYPE.INFO]:          'border-gray-500',
    }[props.type];
});

const shadowClass = computed(() => ({
    [TYPE.DEFAULT]:       'bg-science-blue-800/10',
    [TYPE.PRIMARY]:       'bg-primary-600/10',
    [TYPE.SECONDARY]:     'bg-secondary-950/10',
    [TYPE.PRIMARY_OLD]:   'bg-science-blue-700/10',
    [TYPE.SECONDARY_OLD]: 'bg-science-blue-800/10',
    [TYPE.SUCCESS]:       'bg-emerald-700/10',
    [TYPE.WARNING]:       'bg-orange-600/10',
    [TYPE.DANGER]:        'bg-red-700/10',
    [TYPE.INFO]:          'bg-gray-500/10',
}[props.type]));
</script>

<template>
    <base-popover
        v-model="opened"
        trigger="manual"
        tooltip-class="max-w-[calc(100vw-0.625rem)]"
        :placement="placement"
        :disabled="disabled"
        :no-click-outside="noClickOutside"
        :render="render"
        :open-delay="openDelay"
        :hide-delay="hideDelay"
        :transition="transition"
        @hidden="onHidden"
    >
        <template #reference>
            <div
                ref="referenceRef"
                class="relative flex flex-1 items-center gap-x-2 self-start overflow-hidden rounded-sm border text-gray-500 transition-colors focus:outline-hidden"
                :class="[
                    typeClass,
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer bg-white',
                    {
                        'bg-gray-500/5 opacity-60': disabled,
                        'hover:border-gray-400/80': !disabled && !opened,
                    },
                    {
                        [SIZE.SMALL]: 'min-h-[1.75rem] px-2',
                        [SIZE.MEDIUM]: 'min-h-[2rem] px-2.5',
                        [SIZE.LARGE]: 'min-h-[2.5rem] px-3',
                    }[props.size]
                ]"
                @click.stop="changeOpened"
                @mouseover="onReferenceHover(true)"
                @mouseleave="onReferenceHover(false)"
            >
                <base-icon
                    class="text-gray-300"
                    :class="[
                        {
                            [SIZE.SMALL]: 'text-xs',
                            [SIZE.MEDIUM]: 'text-sm',
                            [SIZE.LARGE]: 'text-base',
                        }[props.size]
                    ]"
                    name="clock"
                />

                <base-input
                    ref="inputRef"
                    :value="formattedValue"
                    class="pointer-events-none grow"
                    wrapper-class="border-none"
                    input-class="grow"
                    :placeholder="placeholder"
                    :size="size"
                    :disabled="disabled"
                />

                <div
                    v-if="showClear"
                    class="ml-1 flex shrink-0 items-center justify-center text-gray-400/80 h-3.5 w-3.5"
                    @click.stop="clear"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke-width="1.5"
                        fill="none" stroke="currentColor"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round"
                              d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                </div>
            </div>
        </template>

        <template>
            <div class="flex flex-col gap-y-2 p-2">
                <div class="relative flex items-center justify-center">
                    <base-time-picker-hours
                        v-model="model"
                        :hours-format="hoursFormat"
                        :type="type"
                    />
                    <base-time-picker-minutes
                        v-model="model"
                        :step="minutesStep"
                        :type="type"
                    />
                    <base-time-picker-seconds
                        v-if="withSeconds"
                        v-model="model"
                        :type="type"
                    />
                    <base-time-picker-am-pm
                        v-if="hoursFormat === HOURS_FORMAT.HOURS_12"
                        v-model="model"
                        :type="type"
                    />

                    <div
                        class="pointer-events-none absolute top-1/2 z-0 h-10 w-full -translate-y-1/2 rounded-sm"
                        :class="shadowClass"
                    />
                </div>

                <div
                    class="flex items-center gap-x-3"
                    :class="{
                        'justify-between': !noSubmit,
                    }"
                >
                    <base-button
                        label="Now"
                        size="small"
                        :type="type"
                        text
                        @click="onNow()"
                    />

                    <base-button
                        v-if="!noSubmit"
                        label="Ok"
                        size="small"
                        :type="type"
                        text
                        @click="changeOpened()"
                    />
                </div>
            </div>
        </template>
    </base-popover>
</template>
